/**
 * DECKSTR.GG — Arena Publisher (Browser Edition)
 * ───────────────────────────────────────────────
 * Publishes game state to Supabase Realtime using the
 * existing browser Supabase client. Replaces the Node
 * WebSocket-based publisher for browser use.
 *
 * Channel: arena-gamestate-{channelName}
 * Events: game_state, card_focus, zone_detail, match_start, match_end
 */

import { supabase } from '../supabase';

// ─── Types ────────────────────────────────────────

export interface CardFocusPayload {
    name: string;
    imageUrl: string;
    grpId?: number;
    transition: string;
    type?: string;
    cmc?: number;
}

export interface ZoneDetailPayload {
    library: {
        total: number;
        uniqueCards: number;
        topDraws: Array<{ name: string; qty: number; drawPct: string }>;
    };
    graveyard: {
        cards: Array<{ name: string; grpId?: number }>;
        total: number;
    };
}

export interface GameStatePayload {
    turn: number | null;
    activePlayer: number | null;
    phase: string | null;
    myLife: number | null;
    oppLife: number | null;
    zones: {
        hand: number;
        library: number;
        graveyard: number;
        exile: number;
        battlefield: number;
        lands: number;
    };
    oppZones: {
        handSize: number;
        creatures: number;
        lands: number;
        openMana: number;
    };
}

// ─── BrowserPublisher Class ───────────────────────

export class BrowserPublisher {
    private _channelName: string;
    private _channel: ReturnType<typeof supabase.channel> | null = null;
    private _connected: boolean = false;
    private _throttleMs: number;
    private _lastPublishTs: number = 0;
    private _onLog: (msg: string) => void;

    constructor({
        channelName,
        throttleMs = 250,
        onLog = () => { },
    }: {
        channelName: string;
        throttleMs?: number;
        onLog?: (msg: string) => void;
    }) {
        this._channelName = channelName;
        this._throttleMs = throttleMs;
        this._onLog = onLog;
    }

    get isConnected(): boolean {
        return this._connected;
    }

    get channelName(): string {
        return this._channelName;
    }

    /**
     * Connect to the Supabase Realtime channel.
     */
    async connect(): Promise<void> {
        const topic = `arena-gamestate-${this._channelName}`;
        this._channel = supabase.channel(topic);

        return new Promise((resolve, reject) => {
            this._channel!.subscribe((status: string) => {
                if (status === 'SUBSCRIBED') {
                    this._connected = true;
                    this._onLog(`Connected to channel: ${topic}`);
                    resolve();
                } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                    this._connected = false;
                    this._onLog(`Channel error: ${status}`);
                    reject(new Error(`Channel subscription failed: ${status}`));
                }
            });
        });
    }

    /**
     * Publish a game state update.
     */
    publishGameState(payload: GameStatePayload): void {
        if (!this._canPublish()) return;
        this._broadcast('game_state', payload);
    }

    /**
     * Publish a card focus event (zone transition → show card on overlay).
     */
    publishCardFocus(payload: CardFocusPayload): void {
        // Card focus events are not throttled — they need to be immediate
        this._broadcast('card_focus', payload);
    }

    /**
     * Publish zone detail (library draw %, graveyard contents).
     */
    publishZoneDetail(payload: ZoneDetailPayload): void {
        this._broadcast('zone_detail', payload);
    }

    /**
     * Publish a match event (start/end).
     */
    publishMatchEvent(eventType: 'match_start' | 'match_end', data: Record<string, any> = {}): void {
        this._broadcast(eventType, data);
    }

    /**
     * Disconnect from the channel.
     */
    disconnect(): void {
        if (this._channel) {
            supabase.removeChannel(this._channel);
            this._channel = null;
        }
        this._connected = false;
        this._onLog('Disconnected');
    }

    // ─── Internal ─────────────────────────────────

    private _canPublish(): boolean {
        const now = Date.now();
        if (now - this._lastPublishTs < this._throttleMs) return false;
        this._lastPublishTs = now;
        return true;
    }

    private _broadcast(event: string, payload: any): void {
        if (!this._channel || !this._connected) return;
        this._channel.send({
            type: 'broadcast',
            event,
            payload,
        });
    }
}
